/**
 * Workspace Context - GoogleSheetsClient 관리
 *
 * 활성 워크스페이스에 따라 적절한 GoogleSheetsClient 인스턴스를 제공합니다.
 * 클라이언트를 캐싱하여 불필요한 재생성을 방지합니다.
 */

import { GoogleSheetsClient } from '@/core/api/GoogleSheetsClient'
import { getCurrentSpreadsheetId } from './workspaceStore'

/**
 * GoogleSheetsClient 캐시
 *
 * spreadsheetId를 키로 하여 클라이언트 인스턴스를 저장합니다.
 * 동일한 spreadsheetId에 대해서는 캐시된 인스턴스를 재사용합니다.
 *
 * @example
 * Key: '1ABC...XYZ', Value: GoogleSheetsClient 인스턴스
 */
const clientCache = new Map<string, GoogleSheetsClient>()

/**
 * 현재 워크스페이스의 GoogleSheetsClient 가져오기
 *
 * Repository에서 호출하여 현재 활성 워크스페이스의
 * GoogleSheetsClient 인스턴스를 가져옵니다.
 *
 * 캐시를 사용하여 동일한 spreadsheetId에 대해서는
 * 기존 인스턴스를 재사용합니다.
 *
 * @returns 현재 워크스페이스의 GoogleSheetsClient
 * @throws {Error} 활성 워크스페이스가 없는 경우
 *
 * @example
 * ```typescript
 * import { getWorkspaceClient } from '@/core/workspace/WorkspaceContext'
 *
 * export class GoogleSheetsTransactionRepository {
 *   private getClient(): GoogleSheetsClient {
 *     return getWorkspaceClient()
 *   }
 *
 *   async findAll() {
 *     const rows = await this.getClient().readSheet('Transactions')
 *     // ...
 *   }
 * }
 * ```
 */
export function getWorkspaceClient(): GoogleSheetsClient {
  const spreadsheetId = getCurrentSpreadsheetId()

  if (!spreadsheetId) {
    throw new Error('활성 워크스페이스가 없습니다. 워크스페이스를 먼저 설정해주세요.')
  }

  // 캐시에 있으면 기존 인스턴스 반환
  if (clientCache.has(spreadsheetId)) {
    return clientCache.get(spreadsheetId)!
  }

  // 없으면 새로 생성하고 캐시에 저장
  const newClient = new GoogleSheetsClient(spreadsheetId)
  clientCache.set(spreadsheetId, newClient)

  return newClient
}

/**
 * 클라이언트 캐시 초기화
 *
 * 워크스페이스 전환 시 호출하여 기존 클라이언트 인스턴스를
 * 모두 제거합니다. 이를 통해 메모리 누수를 방지하고
 * 깨끗한 상태에서 새 워크스페이스를 시작할 수 있습니다.
 *
 * @example
 * ```typescript
 * import { clearClientCache } from '@/core/workspace/WorkspaceContext'
 *
 * const handleSwitchWorkspace = (workspaceId: string) => {
 *   clearClientCache()  // 기존 클라이언트 정리
 *   setActiveWorkspace(workspaceId)
 *   window.location.reload()  // 페이지 새로고침
 * }
 * ```
 */
export function clearClientCache(): void {
  clientCache.clear()
}

/**
 * 특정 spreadsheetId의 클라이언트 제거
 *
 * 특정 워크스페이스의 클라이언트만 캐시에서 제거합니다.
 * 워크스페이스 삭제 시 사용할 수 있습니다.
 *
 * @param spreadsheetId - 제거할 스프레드시트 ID
 *
 * @example
 * ```typescript
 * deleteWorkspace(workspaceId)
 * removeClientFromCache(workspace.spreadsheetId)
 * ```
 */
export function removeClientFromCache(spreadsheetId: string): void {
  clientCache.delete(spreadsheetId)
}
